import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  ArrowRight,
  CheckCircle2,
  Lightbulb,
  Zap,
  Award,
  Share2,
  Users,
  Shield
} from 'lucide-react';

const whyPriceFormsIcons = [Zap, Award, Share2, Users, Shield];

export default function CalculatorExamples() {
  const { t } = useTranslation('calculator-examples');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('calculator-examples', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              <Lightbulb className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-intro">
              {t('hero.intro')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-6 max-w-3xl mx-auto" data-testid="text-description">
              {t('hero.description')}
            </p>

            {Array.isArray(t('hero.benefits', { returnObjects: true })) && (
              <div className="flex flex-col items-center gap-2 mb-8 max-w-2xl mx-auto">
                {(t('hero.benefits', { returnObjects: true }) as string[]).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2" data-testid={`item-hero-benefit-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-lg text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            )}
            
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all" 
              onClick={handleCTAClick}
              data-testid="button-cta-hero"
            >
              {t('hero.cta')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>

          {/* Why Section */}
          <section className="mb-20">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4" data-testid="icon-why">🧩</div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-why">
                {t('why.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-why-description">
                {t('why.description')}
              </p>
              <p className="text-lg text-foreground font-medium max-w-3xl mx-auto" data-testid="text-why-conclusion">
                {t('why.conclusion')}
              </p>
            </div>
          </section>

          {/* Industries Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-industries">
                {t('industries.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-industries-subtitle">
                {t('industries.subtitle')}
              </p>
            </div>
            
            <div className="space-y-8">
              {Array.isArray(t('industries.examples', { returnObjects: true })) && (t('industries.examples', { returnObjects: true }) as any[]).map((example: any, index: number) => (
                <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`card-industry-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-4xl" data-testid={`icon-industry-${index}`}>{example.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-1" data-testid={`title-industry-${index}`}>
                          {example.industry}
                        </h3>
                        <p className="text-lg text-muted-foreground" data-testid={`subtitle-industry-${index}`}>
                          {example.title}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold mb-3" data-testid={`example-title-${index}`}>
                        {example.exampleTitle}
                      </h4>
                      
                      <div className="space-y-2 text-sm">
                        <p data-testid={`example-questions-${index}`}>
                          <span className="font-medium">Questions:</span> {example.questions}
                        </p>
                        <p data-testid={`example-result-${index}`}>
                          <span className="font-medium">Result:</span> {example.result}
                        </p>
                        <p data-testid={`example-cta-${index}`}>
                          <span className="font-medium">CTA:</span> {example.cta}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm" data-testid={`example-why-${index}`}>
                        <span className="font-semibold">Why it works:</span> {example.whyWorks}
                      </p>
                    </div>
                    
                    {example.tip && (
                      <div className="bg-primary/5 border-l-4 border-primary p-3 rounded">
                        <p className="text-sm" data-testid={`example-tip-${index}`}>
                          <span className="font-semibold">💡 Tip:</span> {example.tip}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Common Features Table Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-features">
                {t('commonFeatures.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-features-subtitle">
                {t('commonFeatures.subtitle')}
              </p>
            </div>
            
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="table-features">
                  <thead className="bg-primary/5">
                    <tr>
                      <th className="text-left p-4 font-semibold" data-testid="header-feature">Feature</th>
                      <th className="text-left p-4 font-semibold" data-testid="header-description">Description</th>
                      <th className="text-left p-4 font-semibold" data-testid="header-effect">Effect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(t('commonFeatures.features', { returnObjects: true })) && (t('commonFeatures.features', { returnObjects: true }) as any[]).map((feature: any, index: number) => (
                      <tr key={index} className="border-t" data-testid={`row-feature-${index}`}>
                        <td className="p-4 font-medium" data-testid={`cell-feature-name-${index}`}>
                          {feature.feature}
                        </td>
                        <td className="p-4 text-muted-foreground" data-testid={`cell-feature-description-${index}`}>
                          {feature.description}
                        </td>
                        <td className="p-4" data-testid={`cell-feature-effect-${index}`}>
                          <Badge variant="secondary">{feature.effect}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>

          {/* How to Choose Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-how-to-choose">
                {t('howToChoose.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-how-to-choose-subtitle">
                {t('howToChoose.subtitle')}
              </p>
            </div>
            
            <Card className="mb-6">
              <CardContent className="p-6">
                <ol className="space-y-4">
                  {Array.isArray(t('howToChoose.steps', { returnObjects: true })) && (t('howToChoose.steps', { returnObjects: true }) as string[]).map((step, index) => (
                    <li key={index} className="flex gap-4" data-testid={`item-step-${index}`}>
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <p className="text-lg pt-1">{step}</p>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
            
            <p className="text-center text-lg text-muted-foreground italic" data-testid="text-how-to-choose-conclusion">
              {t('howToChoose.conclusion')}
            </p>
          </section>

          {/* Why PriceForms Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-why-priceforms">
                {t('whyPriceForms.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-why-priceforms-subtitle">
                {t('whyPriceForms.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {Array.isArray(t('whyPriceForms.features', { returnObjects: true })) && (t('whyPriceForms.features', { returnObjects: true }) as any[]).map((feature: any, index: number) => {
                const Icon = whyPriceFormsIcons[index % whyPriceFormsIcons.length];
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`card-priceforms-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-priceforms-${index}`}>
                            {feature.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-priceforms-${index}`}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <p className="text-center text-lg text-foreground font-medium" data-testid="text-why-priceforms-conclusion">
              {t('whyPriceForms.conclusion')}
            </p>
          </section>

          {/* FAQ Section */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-testid="title-faq">
              {t('faq.title')}
            </h2>
            
            <div className="space-y-6">
              {Array.isArray(t('faq.questions', { returnObjects: true })) && (t('faq.questions', { returnObjects: true }) as any[]).map((item: any, index: number) => (
                <Card key={index} data-testid={`card-faq-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3" data-testid={`question-faq-${index}`}>
                      {item.question}
                    </h3>
                    <p className="text-muted-foreground" data-testid={`answer-faq-${index}`}>
                      {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="mb-16 bg-gradient-to-b from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-cta">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-muted-foreground mb-3 font-medium" data-testid="text-cta-subtitle">
              {t('cta.subtitle')}
            </p>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="text-cta-description">
              {t('cta.description')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                onClick={handleCTAClick}
                data-testid="button-cta-primary"
              >
                {t('cta.primaryButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all bg-background"
                onClick={handleCTAClick}
                data-testid="button-cta-secondary"
              >
                {t('cta.secondaryButton')}
              </Button>
            </div>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="calculator-examples" />
      
      <Footer />
    </>
  );
}
